// Движение текста (ux/13 §6.3, ux/14): печать реплик и писем по словам с пропуском по тапу и отсчёт чисел.
// Место под текст занято до анимации: печать лишь проявляет уже свёрстанные слова, число — моноширинные цифры.
import { useLayoutEffect, useRef } from 'preact/hooks';
import { num } from '../i18n/format.js';
import { bump, ease, reduced, tween } from './motion.js';

/**
 * Печать по словам: слова свёрстаны сразу (прозрачными) и проявляются по очереди — ничего не переносится заново.
 * Тап по тексту — показать всё сразу (тап съедается, чтобы не сработать «мимо»). cps — знаков в секунду.
 */
export function Typed(p: { text: string; cps?: number; class?: string; onDone?: () => void; testId?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const cps = p.cps ?? 55;
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const words = [...root.querySelectorAll<HTMLElement>('.tw')];
    const finish = () => { for (const w of words) w.classList.add('on'); root.classList.add('tw-done'); p.onDone?.(); };
    root.classList.remove('tw-done');
    for (const w of words) w.classList.remove('on');
    if (reduced() || !words.length) { finish(); return; }
    let i = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const next = () => {
      if (i >= words.length) { finish(); return; }
      const w = words[i++]!;
      w.classList.add('on');
      timer = setTimeout(next, 30 + (w.textContent?.length ?? 1) * (1000 / cps));
    };
    timer = setTimeout(next, 90);
    const skip = (e: Event) => {
      if (root.classList.contains('tw-done')) return;
      clearTimeout(timer);
      finish();
      e.stopPropagation();
      e.preventDefault();
    };
    root.addEventListener('click', skip);
    return () => { clearTimeout(timer); root.removeEventListener('click', skip); };
  }, [p.text]);
  const parts = p.text.split(/(\s+)/);
  return (
    <span ref={ref} class={`typed ${p.class ?? ''}`} data-testid={p.testId}>
      {parts.map((w, i) => (w === '' ? null : /^\s+$/.test(w) ? w : <span key={i} class="tw">{w}</span>))}
    </span>
  );
}

/**
 * Число с отсчётом: при смене значения отсчитывает от прежнего (0.4–0.9 с, дольше — для больших разниц),
 * ближайшую «пилюлю» (bump) толкает, рост красит зелёным, убыль — красным. from — первый показ с отсчётом
 * (награды «набегают»); без from первый показ сразу конечный.
 */
export function Count(p: { value: number; format?: (n: number) => string; from?: number; delay?: number; duration?: number; bump?: string | false; class?: string; testId?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const shown = useRef(p.from ?? p.value);
  const fmt = p.format ?? num;
  useLayoutEffect(() => {
    const el = ref.current;
    const a = shown.current;
    const b = p.value;
    if (!el || a === b) return;
    const dur = p.duration ?? Math.min(900, 400 + 120 * Math.log10(Math.abs(b - a) + 1));
    const host = p.bump === false ? null : (el.closest(p.bump ?? '.res-pill, .badge') as HTMLElement | null);
    const dir = b > a ? 'up' : 'down';
    // Пишем в тот же текстовый узел, что держит Preact: замена узла (textContent) отвязала бы его от диффа.
    const node = el.firstChild instanceof Text ? el.firstChild : el.appendChild(document.createTextNode(''));
    const t = tween(dur, (k) => {
      const v = a + (b - a) * k;
      shown.current = k >= 1 ? b : v;
      node.data = fmt(k >= 1 ? b : Math.round(v));
    }, {
      ease: ease.outCubic,
      delay: p.delay ?? 0,
      onDone: () => { el.classList.remove('count-up', 'count-down'); },
    });
    if (!reduced()) {
      el.classList.remove('count-up', 'count-down');
      el.classList.add(`count-${dir}`);
      if (host) setTimeout(() => bump(host, dir === 'up' ? 1.12 : 1.06), p.delay ?? 0);
    }
    return () => t.cancel();
  }, [p.value]);
  return <span ref={ref} class={`count ${p.class ?? ''}`} data-testid={p.testId}>{fmt(Math.round(shown.current))}</span>;
}
